import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterModal({ open, onClose }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSent(false);
      setEmail('');
      setError('');
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: 'modal' });
      setSent(true);
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('newsletter subscribe failed', err);
      setError("Sorry, that didn't go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#2C3A28]/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-3xl bg-[#E9EBED] shadow-2xl overflow-hidden fade-up">
        <div className="h-2 bg-gradient-to-r from-[#2C3A28] via-[#2C3A28] to-[#2C3A28]" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#2C3A28] hover:bg-[#EDEAE0] transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="px-8 pt-9 pb-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[#EDEAE0] flex items-center justify-center text-[#2C3A28]">
            <Mail size={26} />
          </div>
          <h3 className="mt-5 font-display text-3xl font-semibold text-[#2C3A28]">
            Join the family
          </h3>
          <p className="mt-2 text-[#3B2A20]/70 leading-relaxed">
            Be first to hear about new milks, seasonal recipes and events at the cafe.
            No spam, promise.
          </p>

          {sent ? (
            <div className="mt-7 rounded-2xl bg-[#2C3A28]/12 border border-[#2C3A28]/25 px-5 py-6">
              <p className="font-display text-2xl text-[#5C6738]">Grazie mille!</p>
              <p className="text-[#3B2A20]/70 mt-1">You're officially on the list.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full rounded-full border border-[#2C3A28]/15 bg-white px-6 py-4 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-[#2C3A28] text-white py-4 font-semibold hover:bg-[#2C3A28] transition-colors disabled:opacity-60"
              >
                {busy ? 'Subscribing...' : 'Subscribe'}
              </button>
              {error && (
                <p className="text-sm text-[#2C3A28]" data-testid="newsletter-error">
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
